<template>
  <div class="club-info" :class="{ 'club-info--no-logo': !club.logo }">
    <img
      v-if="club.logo"
      :src="club.logo"
      :alt="club.name"
      class="club-logo"
    />
    <div class="club-details">
      <h2 class="club-name">{{ club.name }}</h2>
      <div class="club-meta">
        <div class="club-address">
          <BookingLocationIcon :size="16" :primary-color="primaryColor" class="meta-icon" />
          <span>{{ club.address }}</span>
        </div>
        <div class="club-schedule">
          <BookingPhoneIcon :size="16" :primary-color="primaryColor" class="meta-icon" />
          <span>{{ primaryClubPhone || 'Не указано' }}</span>
        </div>
      </div>
    </div>
    <button class="arrow-button" @click="openModal">
      <img :src="arrowIcon" alt="" class="arrow-icon" />
    </button>
  </div>

  <div v-if="isModalOpen" class="branch-modal-overlay" @click.self="closeModal">
    <div class="branch-modal">
      <div class="branch-modal-header">
        <button
          v-if="isPreviewStep"
          type="button"
          class="branch-modal-back"
          @click="goBackToList"
        >
          <img :src="backIcon" alt="Назад" />
        </button>
        <div class="branch-modal-title">Выбор филиала</div>
        <button type="button" class="branch-modal-close" @click="closeModal">
          <img :src="closeIcon" alt="Закрыть" />
        </button>
      </div>

      <template v-if="!isPreviewStep">
        <div class="branch-search-wrapper">
          <input
            v-model.trim="searchQuery"
            type="text"
            class="branch-search"
            placeholder="Введите адрес или название клуба"
          />
          <img :src="searchIcon" alt="" class="branch-search-icon" />
        </div>

        <div class="branch-list">
          <button
            v-for="branch in filteredBranches"
            :key="branch.id"
            type="button"
            class="branch-item"
            :class="{ active: draftBranchId === branch.id }"
            @click="selectBranch(branch)"
          >
            <div class="branch-item-content">
              <div class="branch-item-name">{{ branch.name }}</div>
              <div v-if="branch.address" class="branch-item-address">{{ branch.address }}</div>
            </div>
            <div v-if="draftBranchId === branch.id" class="branch-item-check">
              <img :src="checkIcon" alt="" />
            </div>
          </button>
        </div>
      </template>

      <div
        v-else
        ref="branchPreview"
        class="branch-preview"
        :class="{ 'branch-preview-scrollable': hasPreviewScroll }"
      >
        <div class="branch-preview-content">
          <h3 class="branch-preview-name">{{ previewBranch.name }}</h3>
          <div class="branch-preview-address">{{ previewBranch.address }}</div>

          <div class="branch-contact-card">
            <div class="branch-contact-row">
              <div class="branch-contact-label">Связаться</div>
              <div v-if="parsedBranchPhones.length > 0" class="branch-contact-values">
                <a
                  v-for="phone in parsedBranchPhones"
                  :key="phone"
                  class="branch-contact-value"
                  :href="`tel:${phone}`"
                >
                  {{ phone }}
                </a>
              </div>
              <div v-else class="branch-contact-value">Не указано</div>
            </div>
            <div class="branch-contact-row">
              <div class="branch-contact-label">Сайт</div>
              <a
                v-if="previewBranch.site"
                class="branch-contact-value"
                :href="formattedBranchSiteUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ previewBranch.site }}
              </a>
              <div v-else class="branch-contact-value">Не указано</div>
            </div>
            <div class="branch-contact-row">
              <div class="branch-contact-label">Расписание</div>
              <div class="branch-contact-value">{{ previewBranch.workDays || 'Не указано' }}</div>
            </div>
          </div>

          <div v-if="visibleSocials.length > 0" class="branch-socials-card">
            <a
              v-for="social in visibleSocials"
              :key="social.id || social.type"
              class="branch-social-link"
              :href="social.url"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img :src="social.icon" :alt="social.type" class="branch-social-icon" />
            </a>
          </div>

          <div class="branch-description-card">
            <div class="branch-section-title">Описание</div>
            <div
              class="branch-description-text"
              :class="{ collapsed: !isDescriptionExpanded }"
            >
              {{ previewBranch.description || 'Описание отсутствует' }}
            </div>
            <button
              v-if="shouldShowDescriptionToggle"
              type="button"
              class="branch-description-toggle"
              @click="expandDescription"
            >
              Подробнее
            </button>
          </div>

          <div v-if="previewBranchPhotos.length > 0" class="branch-gallery">
            <div class="branch-gallery-track" ref="galleryTrack">
              <img
                v-for="(photo, index) in previewBranchPhotos"
                :key="`${previewBranch.id}-${index}`"
                :src="photo"
                :alt="`${previewBranch.name} - ${index + 1}`"
                class="branch-gallery-image"
                @load="updatePreviewScrollState"
              />
            </div>

            <div v-if="previewBranchPhotos.length > 1" class="branch-gallery-controls">
              <button
                type="button"
                class="branch-gallery-button"
                @click="scrollGallery('prev')"
              >
                <img :src="backIcon" alt="Предыдущее фото" />
              </button>

              <button
                type="button"
                class="branch-gallery-button"
                @click="scrollGallery('next')"
              >
                <img :src="arrowIcon" alt="Следующее фото" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <button
        v-if="isPreviewStep"
        type="button"
        class="branch-select-button"
        @click="applySelectedBranch"
      >
        Выбрать этот клуб
      </button>
    </div>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, nextTick, ref } from 'vue'
import arrowIcon from '@/widgets/booking/assets/img/icons/arrow_right.svg'
import backIcon from '@/widgets/booking/assets/img/icons/arrow_left.svg'
import closeIcon from '@/widgets/booking/assets/img/icons/booking-close.svg'
import checkIcon from '@/assets/img/check.svg'
import searchIcon from '@/widgets/booking/assets/img/icons/search.svg'
import telegramIcon from '@/widgets/booking/assets/img/icons/social-telegram.svg'
import facebookIcon from '@/widgets/booking/assets/img/icons/social-facebook.svg'
import instagramIcon from '@/widgets/booking/assets/img/icons/social-instagram.svg'
import vkIcon from '@/widgets/booking/assets/img/icons/social-vk.svg'
import whatsappIcon from '@/widgets/booking/assets/img/icons/social-whatsapp.svg'
import linkIcon from '@/widgets/booking/assets/img/icons/social-link.svg'
import BookingLocationIcon from '@/widgets/booking/components/icons/BookingLocationIcon.vue'
import BookingPhoneIcon from '@/widgets/booking/components/icons/BookingPhoneIcon.vue'

const socialIconMap = {
  telegram: telegramIcon,
  facebook: facebookIcon,
  instagram: instagramIcon,
  vk: vkIcon,
  whatsapp: whatsappIcon,
  whastapp: whatsappIcon
}

const props = defineProps({
  club: {
    type: Object,
    required: true
  },
  branches: {
    type: Array,
    default: () => []
  },
  selectedBranchId: {
    type: Number,
    default: 0
  },
  primaryColor: {
    type: String,
    default: '#3C67D6'
  }
})

const emit = defineEmits(['change-branch'])

const isModalOpen = ref(false)
const searchQuery = ref('')
const draftBranchId = ref(0)
const previewBranchId = ref(0)
const modalStep = ref('list')
const galleryTrack = ref(null)
const branchPreview = ref(null)
const isDescriptionExpanded = ref(false)
const hasPreviewScroll = ref(false)

function stripSeconds(value) {
  if (typeof value !== 'string') {
    return ''
  }
  return value.replace(/(\d{1,2}:\d{2}):\d{2}/g, '$1')
}

const formattedSchedule = computed(() => stripSeconds(props.club?.schedule))

const primaryClubPhone = computed(() => {
  const preservedSchedule = formattedSchedule.value
  const phone = props.club?.phone || ''

  void preservedSchedule

  return phone
    .split(',')
    .map((item) => item.trim())
    .find(Boolean) || ''
})


const filteredBranches = computed(() => {
  const query = searchQuery.value.toLowerCase()

  if (!query) {
    return props.branches
  }

  return props.branches.filter((branch) => {
    return [branch.name, branch.address]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(query))
  })
})

const isPreviewStep = computed(() => modalStep.value === 'preview')

const previewBranch = computed(() => {
  return props.branches.find((branch) => branch.id === previewBranchId.value) || props.club
})

const previewBranchPhotos = computed(() => {
  if (Array.isArray(previewBranch.value?.photos) && previewBranch.value.photos.length > 0) {
    return previewBranch.value.photos
  }

  if (previewBranch.value?.logo) {
    return [previewBranch.value.logo]
  }

  return []
})

const parsedBranchPhones = computed(() => {
  const phone = previewBranch.value?.phone || ''

  return phone
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
})

const visibleSocials = computed(() => {
  const socials = Array.isArray(previewBranch.value?.socials) ? previewBranch.value.socials : []

  return socials
    .map((social) => ({
      ...social,
      icon: socialIconMap[social.type] || linkIcon
    }))
})

const shouldShowDescriptionToggle = computed(() => {
  const description = previewBranch.value?.description || ''
  return description.length > 180 && !isDescriptionExpanded.value
})

const formattedBranchSiteUrl = computed(() => {
  const site = previewBranch.value?.site || ''

  if (!site) {
    return '#'
  }

  if (/^https?:\/\//i.test(site)) {
    return site
  }

  return `https://${site}`
})

function openModal() {
  draftBranchId.value = props.selectedBranchId
  previewBranchId.value = props.selectedBranchId
  searchQuery.value = ''
  modalStep.value = 'list'
  isDescriptionExpanded.value = false
  hasPreviewScroll.value = false
  isModalOpen.value = true
}

function closeModal() {
  isModalOpen.value = false
  searchQuery.value = ''
  draftBranchId.value = props.selectedBranchId
  previewBranchId.value = props.selectedBranchId
  modalStep.value = 'list'
  isDescriptionExpanded.value = false
  hasPreviewScroll.value = false
}

function goBackToList() {
  modalStep.value = 'list'
  isDescriptionExpanded.value = false
  hasPreviewScroll.value = false
}

async function selectBranch(branch) {
  previewBranchId.value = branch.id
  modalStep.value = 'preview'
  isDescriptionExpanded.value = false

  await nextTick()
  resetGalleryScroll()
  updatePreviewScrollState()
}

function applySelectedBranch() {
  if (!previewBranchId.value) {
    return
  }

  draftBranchId.value = previewBranchId.value

  if (previewBranchId.value !== props.selectedBranchId) {
    emit('change-branch', previewBranchId.value)
  }

  closeModal()
}

function resetGalleryScroll() {
  if (galleryTrack.value) {
    galleryTrack.value.scrollLeft = 0
  }
}

function scrollGallery(direction) {
  if (!galleryTrack.value) {
    return
  }

  const scrollAmount = 280 + 12
  const left = direction === 'prev' ? -scrollAmount : scrollAmount
  galleryTrack.value.scrollBy({ left, behavior: 'smooth' })
}

function updatePreviewScrollState() {
  if (!branchPreview.value) {
    hasPreviewScroll.value = false
    return
  }

  hasPreviewScroll.value = branchPreview.value.scrollHeight > branchPreview.value.clientHeight + 1
}

async function expandDescription() {
  isDescriptionExpanded.value = true
  await nextTick()
  updatePreviewScrollState()
}
</script>

<style scoped lang="scss">
@import '@/widgets/booking/assets/scss/variables.scss';

.club-info {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #F3F5F8;
  border-radius: 12px;
  margin-bottom: $spacing-lg;
  height: 108px;
  opacity: 1;
}

.club-info--no-logo .club-details {
  padding-left: 0;
}

.club-logo {
  width: 74px;
  height: 74px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: #FFFFFF;
}

.club-details {
  flex: 1;
  min-width: 0;
}

.club-name {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  color: #333333;
  margin: 0 0 8px 0;
}

.club-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.club-address,
.club-schedule {
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: #757885;

  span {
    line-height: 1.4;
    min-width: 0;
  }
}

.club-address span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meta-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  filter: var(--booking-widget-primary-icon-filter, none);
}

.arrow-button {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #FFFFFF;
  border-radius: 7px;
  flex-shrink: 0;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
}

.arrow-icon {
  width: 16px;
  height: 16px;
  display: block;
  filter: var(--booking-widget-primary-icon-filter, none);
}

.branch-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.5);
}

.branch-modal {
  width: min(480px, 100%);
  min-height: 486px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  padding: 20px;
  box-sizing: border-box;
  border-radius: 24px;
  background: #FFFFFF;
}

.branch-modal-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.branch-modal-title {
  color: #252525;
  font-size: 17px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
}

.branch-modal-back,
.branch-modal-close {
  position: absolute;
  top: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 7px;
  background: #F3F5F8;
  padding: 0;
}

.branch-modal-back {
  left: 0;
}

.branch-modal-close {
  right: 0;
}

.branch-modal-back img,
.branch-modal-close img {
  display: block;
}

.branch-modal-back img {
  filter: brightness(0) saturate(100%) invert(52%) sepia(8%) saturate(409%) hue-rotate(191deg) brightness(90%) contrast(88%);
}

.branch-search-wrapper {
  position: relative;
}

.branch-search {
  width: 100%;
  height: 40px;
  margin-bottom: 16px;
  padding: 0 16px 0 48px;
  box-sizing: border-box;
  border: 1px solid transparent;
  border-radius: 12px;
  outline: none;
  background: #F7F9FB;
  color: #252525;
  font-size: 14px;
  font-weight: 400;
}

.branch-search:focus {
  border-color: var(--booking-widget-primary-color, #3C67D6);
}

.branch-search::placeholder {
  color: #9898A5;
}

.branch-search-icon {
  position: absolute;
  top: 10px;
  left: 16px;
  width: 20px;
  height: 20px;
  display: block;
  pointer-events: none;
}

.branch-list,
.branch-preview {
  flex: 1;
  min-height: 0;
}

.branch-list {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.branch-item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border: 1px solid transparent;
  border-radius: 12px;
  background: #F7F9FB;
  text-align: left;
}

.branch-item.active {
  border-color: var(--booking-widget-primary-color, #3C67D6);
  background: var(--booking-widget-primary-color-soft, #3C67D61A);
}

.branch-item-content {
  min-width: 0;
  padding-right: 12px;
}

.branch-item-name {
  color: #252525;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.branch-item-address {
  margin-top: 2px;
  color: #757885;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
}

.branch-item-check {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.branch-item-check img {
  width: 20px;
  height: 20px;
  display: block;
}

.branch-preview {
  overflow-y: auto;
}

.branch-preview.branch-preview-scrollable {
  padding-right: 8px;
  margin-right: -8px;
}

.branch-preview-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 12px;
}

.branch-preview-name {
  margin: 0;
  color: #252525;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
}

.branch-preview-address {
  margin-top: -8px;
  color: #757885;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.branch-contact-card,
.branch-socials-card,
.branch-description-card {
  padding: 16px;
  border-radius: 12px;
  background: #F7F9FB;
}

.branch-contact-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.branch-contact-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.branch-contact-label {
  color: #757885;
  font-size: 10px;
  font-weight: 400;
  line-height: 14px;
  flex-shrink: 0;
}

.branch-contact-value {
  color: var(--booking-widget-primary-color, #3C67D6);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-decoration: none;
  word-break: break-word;
}

.branch-contact-values {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.branch-contact-value:hover {
  color: #335ac0;
}

.branch-description-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.branch-socials-card {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.branch-social-link {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 8px;
  background: var(--booking-widget-primary-color, #3C67D6);
}

.branch-social-icon {
  width: 20px;
  height: 20px;
  display: block;
}

.branch-section-title {
  color: #333333;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
}

.branch-description-text {
  color: #333333;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  white-space: pre-line;
}

.branch-description-text.collapsed {
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.branch-description-toggle {
  width: fit-content;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--booking-widget-primary-color, #3C67D6);
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
}

.branch-contact-row:last-child .branch-contact-value {
  color: #333333;
  white-space: pre-line;
}

.branch-gallery {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.branch-gallery-track {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.branch-gallery-track::-webkit-scrollbar {
  display: none;
}

.branch-gallery-image {
  width: 280px;
  min-width: 280px;
  height: 180px;
  object-fit: cover;
  border-radius: 12px;
  background: #F3F5F8;
}

.branch-gallery-controls {
  display: flex;
  justify-content: flex-start;
  gap: 8px;
}

.branch-gallery-button {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.92);
  padding: 0;
}

.branch-gallery-button img {
  width: 16px;
  height: 16px;
  display: block;
  filter: var(--booking-widget-primary-icon-filter, none);
}

.branch-select-button {
  position: sticky;
  bottom: 0;
  z-index: 2;
  width: 100%;
  min-height: 44px;
  margin-top: 16px;
  border: none;
  border-radius: 12px;
  background: var(--booking-widget-primary-color, #3C67D6);
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
  box-shadow: 0 -12px 24px rgba(255, 255, 255, 0.92);
}
</style>
